import i18n from '@/plugins/i18n'
import { getI18n } from '@/plugins/i18n'

import { useLookup, useDefaultDialogTitle } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'

import { useValidation } from '@/composables/validation'
import { useSubjectService } from '@/services/subject.service'
import { usePriceService } from '@/services/price.service'
import { useDocumentService } from '@/services/document.service'

export const useDialogTitle = () => {
  const { t } = i18n.global
  const { defaultActionTitle } = useDefaultDialogTitle()
  const registryStore = useRegistryStore()

  const itemTitle = (i) => {
    const customer = registryStore.customers.find(c => c.id === i.customer_id) || null
    return i ? `${i.number_mnemonic}/${i.series} ${customer ? customer?.name : ''}` : ''
  }

  const dialogTitle = (i) => {
    const title = `${defaultActionTitle(i)} ${t('common.worksheet')}`
    return i.id ? `${title} - ${itemTitle(i)}` : title
  }

  return { dialogTitle, itemTitle }
}

export const useColumns = (
  editors,
  currentCountryId = {},
  currentStateId = {},
  currentCustomerTypeId = {},
  currentCustomerSubTypeId = {},
  currentCarService = {},
  currentVehicleFuelType = {},
  formInstance = {},
  includesVat = {},
  selectedInterventionTypeId = {},
  currentCustomerVatCountryId = {},
  currentCustomerVatCountryCode = {},
  currentInfoBikeBrandCode = {},
  currentInfoBikeModelCode = {},
  setCellValueZipCode,
  setCellValueCity
) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const garageStore = useGarageStore()
  const portalStore = usePortalStore()
  const { setSubjectType, isVatCountryIta, isNameExtMandatory } = useSubjectService()
  const { validateTaxCode } = useValidation()
  const { getIncludesVatValue } = usePriceService()
  const { setVehicleNextRevisionDueDate, setIncludesVat } = useDocumentService()

  const { withDocumentSeriesAndNumberMnemonicUpdate: _withUpdate } = dataGridColumn.useDocumentSeriesAndNumberMnemonicUpdate()
  const trackedDocumentIdFields = ['deposit', 'garage_reason_ref', 'vehicle']
  const withDocumentSeriesAndNumberMnemonicUpdate = (fn) =>
    _withUpdate(fn, trackedDocumentIdFields, editors, garageStore.api.getWorksheetNextIdByDataAsync)

  const toggleReadOnlyValidation = (field) => ({
    type: 'custom',
    message: i18n.t('common.required'),
    reevaluate: true,
    validationCallback: (e) => {
      const intervention_type_ref = e.data.intervention_type_ref

      if (intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
        return e.value != null && e.value !== ''
      }

      return true
    }
  })

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', option.data?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '10rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    dataGridColumn.useDocumentDateColumn({validationRules: [{ type: 'required' }]}),
    dataGridColumn.useSeriesColumn({visible: false}),
    dataGridColumn.useNumberMnemonicColumn(),
    { dataField: 'status', caption: i18n.t('common.status'), lookup: useLookup({ store: portalStore.getLocalizedDocumentStatuses() }) },
    dataGridColumn.useDocumentProgressStatusColumn({
      caption: i18n.t('common.documentProgressStatus'),
      dataField: 'document_progress_status',
      formInstance: formInstance,
    }),
    dataGridColumn.useCustomerColumn({
      caption: i18n.t('common.owner'),
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useCustomerColumn().setCellValue(newData, value)
        newData.customer = value || { ...webSiteStore.emptyCustomer }
        newData.customer_id = newData.customer?.id
        if (currentData.rows_count > 0) {
          newData.customer.vat_type_ref = null
        }
        if (currentCountryId) {
          currentCountryId.value = value?.address?.country
        }
        if (currentStateId) {
          currentStateId.value = value?.address?.state
        }
        if (!currentData.rows_count) {
          newData.vat_type_ref = newData.customer.vat_type_ref
        }
        const currentCustomer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === newData.customer?.id) ?? null
        const currentBillingCustomer = registryStore.customers.find(c => c.id === currentCustomer?.invoice_to_customer) ?? null

        newData.invoice_to_customer = currentBillingCustomer?.id ?? null
        newData.singleInvoice = currentBillingCustomer?.single_invoice || currentCustomer?.single_invoice || null

        setIncludesVat(newData, value, currentData, includesVat, currentCustomer)
        newData.payment_type_ref = currentCustomer?.accounting_payment_type_ref
        newData.currency_ref = currentCustomer?.accounting_currency_ref || webSiteStore.getDefaultCurrency()?.id
        currentCountryId.value = currentCustomer?.address?.country
        currentStateId.value = currentCustomer?.address?.state

        if (value) {
          setSubjectType(currentCustomer, currentData, currentCustomer.subject_type_ref, currentCustomer.subject_sub_type_ref)
        }
      },
    }),
    {
      dataField: 'infoBike.plate_number',
      caption: ' ',
      editCellTemplate: 'infoBikePlateNumberEditor',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useInfoBikeBrandColumn(
      {
        visible: false,
        showInColumnChooser: false
      }
    ),
    dataGridColumn.useInfoBikeModelColumn({
      brand_code: currentInfoBikeBrandCode,
      visible: false,
      showInColumnChooser: false
    }),
    dataGridColumn.useInfoBikeVariantColumn({
      model_code: currentInfoBikeModelCode,
      visible: false,
      showInColumnChooser: false
    }),
    {
      dataField: 'infoBike.infoBike',
      caption: i18n.t('common.infoBike'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useVehicleColumn({
      caption: i18n.t('common.vehicle'),
      dataField: 'vehicle_id',
      editCellTemplate: 'vehicleEditor',
      formInstance: formInstance,
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useVehicleColumn().setCellValue(newData, value)
        if (value?.preserveOwner) {
          newData.customer = value.preservedCustomer ?? currentData.customer
          newData.customer_id = value.preservedCustomerId ?? currentData.customer_id
        }
        if (value?.id) {
          newData.vehicle = (await registryStore.getOrFetchVehiclesAsync()).find(i => i.id === value.id)
        } else {
          newData.vehicle = { ...webSiteStore.emptyVehicle }
          if (!value?.preserveOwner) {
            newData.customer = { ...webSiteStore.emptyCustomer }
            newData.customer_id = null
          }
        }
        newData.vehicle_id = newData.vehicle?.id

        if (value?.customer_id && !value?.preserveOwner) {
          newData.customer = {}
          newData.customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === value.customer_id)
          if (newData.customer) {
            newData.customer_id = newData.customer.id
            newData.customer.vat_type_ref = newData.customer.accounting_vat_type_ref
            newData.invoice_to_customer = newData.customer?.invoice_to_customer
            const currentBillingCustomer = registryStore.customers.find(c => c.id === newData.customer?.invoice_to_customer) ?? null
            newData.singleInvoice = currentBillingCustomer?.single_invoice || newData.customer?.single_invoice || null
          }
          setIncludesVat(newData, value, currentData, includesVat, newData.customer)
          newData.payment_type_ref = newData.customer?.accounting_payment_type_ref
        }

        if (newData.vehicle?.id) {
          const res = await registryStore.api.getVehicleNextCarServiceAsync(newData.vehicle.id)
          currentCarService.value = res.data

          if (currentData.intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
            newData.car_service = res.data
          }
        }

        setVehicleNextRevisionDueDate(newData)

        currentVehicleFuelType.value = newData.vehicle?.fuel_type ?? null
      },
    }),
    dataGridColumn.useSupplierBrandColumn({
      dataField: 'vehicle.supplier_brand_ref',
      all: true,
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }],
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => {
        if (!newData.vehicle) newData.vehicle = {}
        newData.vehicle.supplier_brand_ref = value
      }),
    }),
    {
      dataField: 'vehicle.infoBikeCodeFull',
      caption: i18n.t('common.infoBike'),
      visible: false,
      showInColumnChooser: false,
      editorOptions: {
        readOnly: true,
      },
    },
    {
      dataField: 'vehicle.name',
      caption: i18n.t('common.description'),
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }]
    },
    {
      dataField: 'vehicle.color',
      caption: i18n.t('common.color'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.usePlateNumberColumn(),
    dataGridColumn.useVinColumn({ dataField: 'vehicle.vin', visible: false }),
    dataGridColumn.useEngineSizeColumn({ dataField: 'vehicle.engine_size' }),
    {
      caption: i18n.t('common.engineCode'),
      dataField: 'vehicle.engine_code',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useVehicleKmColumn(),
    {
      caption: i18n.t('common.enrollmentDate'),
      dataField: 'vehicle.enrollment_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.saleDate'),
      dataField: 'vehicle.sale_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.revisionDueDate'),
      dataField: 'vehicle.revision_due_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useNextRevisionDueDateColumn(),
    dataGridColumn.useInterventionTypeColumn(),
    dataGridColumn.useReferenceDocumentColumn({
      dataField: 'quote.id',
      caption: i18n.t('common.generatingDocumentRef'),
      editCellTemplate: 'quoteRef',
      cellTemplate: 'quoteRef',
    }),
    dataGridColumn.useReferenceDocumentColumn({
      dataField: 'sale_document.id',
      caption: i18n.t('common.generatedDocumentRef'),
      editCellTemplate: 'saleDocumentRef',
      cellTemplate: 'saleDocumentRef',
    }),
    dataGridColumn.useTotalNoVatColumn(),
    dataGridColumn.useTotalVatColumn(),
    // Optional columns
    {
      caption: i18n.t('common.workReason'),
      dataField: 'garage_reason_ref',
      lookup: useLookup({ store: garageStore.getLocalizedGarageReasons() }),
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate(async(newData, value, currentData) => {
        newData.garage_reason_ref = value
        const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)
        newData.intervention_type_ref = currentGarageReason?.intervention_type_ref
        newData.work_type = currentGarageReason?.work_type
        newData.work_value = currentGarageReason?.work_value

        const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)
        selectedInterventionTypeId.value = interventionType?.id
        const hasCarService = interventionType?.has_car_service

        editors['car_service']?.option('disabled', !hasCarService)

        const isCarService = newData.intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id
        if (!hasCarService || !isCarService) {
          newData.car_service = null
          return
        }

        if (currentCarService.value) {
          newData.car_service = currentCarService.value
          return
        }

        if (currentData?.vehicle?.id) {
          const res = await registryStore.api.getVehicleNextCarServiceAsync(currentData.vehicle.id)
          currentCarService.value = newData.car_service = res.data
          return
        }
      }),
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.mechanic'),
      dataField: 'mechanics',
      editCellTemplate: 'mechanicsEditor',
      cellTemplate: 'mechanicsAvatarList',
      lookup: useLookup({
        store: webSiteStore.userDetails.filter(i => i.is_mechanic),
        sort: 'name',
        displayExpr: (item) => `${item.name} ${item.name_ext}`,
      }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.tag', 2),
      dataField: 'tags',
      editCellTemplate: 'tagsEditor',
      cellTemplate: 'tagsEditor',
      visible: false,
      showInColumnChooser: false,
    },
    // Hidden columns
    dataGridColumn.useVatTypeColumn({ dataField: 'vat_type_ref', caption: i18n.t('common.accountingVat'), visible: false, showInColumnChooser: false, validationRules: [] }),
    {
      dataField: 'customer.subject_type_ref',
      caption: i18n.t('common.personType'),
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerTypeId.value = value
        currentCustomerSubTypeId.value = null
        setSubjectType(newData.customer, currentData, value)
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(currentData.customer?.vat_country_id))
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.subject_sub_type_ref',
      caption: `${i18n.t('common.category')} / ${i18n.t('common.gender')}`,
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectSubTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerSubTypeId.value = value
        setSubjectType(newData.customer, currentData, currentData.customer?.subject_type_ref, value)
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressCountryColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueCountry(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressStateColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueState(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressCityColumn({ setCellValue: (newData, value, currentData) => setCellValueCity(newData, value, currentData) }),
    { dataField: 'customer.code', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.code')}`},
    { dataField: 'customer.name', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.nameAlt')}`},
    {
      dataField: 'customer.name_ext',
      visible: false,
      showInColumnChooser: false,
      caption: `${i18n.t('common.customer')} ${i18n.t('common.nameExt')}`,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => !isNameExtMandatory(e.data?.customer?.subject_type_ref) || !!e.value,
      }],
    },
    {
      caption: `${i18n.t('common.customer')} ${i18n.t('common.vatCountry')}`,
      dataField: 'customer.vat_country_id',
      editCellTemplate: 'vatCountryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        newData.customer.vat_country_id = value?.id
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(newData.customer?.vat_country_id))
      },
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressStreetColumn(),
    dataGridColumn.useCustomerVatNumberColumn({ vatCountryCode: currentCustomerVatCountryCode.value, vatCountryId: currentCustomerVatCountryId.value, subjectSubTypeId: currentCustomerSubTypeId.value }),
    dataGridColumn.useTaxCodeColumn(
      {
        dataField: 'customer.tax_code',
        caption: `${i18n.t('common.customer')} ${i18n.t('common.taxCode')}`,
        validationRules: [{
          type: 'custom',
          reevaluate: true,
          validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateTaxCode(e.value, currentCustomerVatCountryCode.value),
        }],
        visible: false,
        showInColumnChooser: false,
      }
    ),
    { dataField: 'customer.address.country_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.country')}`, visible: false, showInColumnChooser: false},
    { dataField: 'customer.address.state_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.state')}`, visible: false, showInColumnChooser: false},
    { dataField: 'customer.address.city_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.city')}`, visible: false, showInColumnChooser: false},
    dataGridColumn.useCustomerAddressZipCodeColumn({setCellValueZipCode: (newData, value, currentData, type) => setCellValueZipCode(newData, value, currentData, 'customer', false)}),
    dataGridColumn.useEmailColumn({ dataField: 'customer.email', caption: `${i18n.t('common.customer')} ${i18n.t('common.email')}`}),
    dataGridColumn.useCustomerMobileColumn(),
    { dataField: 'customer.phone', caption: `${i18n.t('common.customer')} ${i18n.t('common.phone')}`, visible: false, showInColumnChooser: false},
    dataGridColumn.useDepositColumn({
      visible: false,
      showInColumnChooser: false,
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => { newData.deposit = value?.id }),
    }),
    dataGridColumn.useDepositColumn({dataField: 'garage', caption: i18n.t('common.garage'), visible: false, showInColumnChooser: false}),
    {
      caption: i18n.t('common.carService'),
      dataField: 'car_service',
      dataType: 'number',
      editCellTemplate: 'carServiceNumberEditor',
      visible: false,
      showInColumnChooser: false,
      validationRules: [toggleReadOnlyValidation('car_service')],
    },
    {
      caption: i18n.t('common.workType'),
      dataField: 'work_type',
      lookup: useLookup({
        store: garageStore.getLocalizedWorkTypes(),
        displayExpr: (item) => item.name
      }),
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }]
    },
    {
      caption: i18n.t('common.workValue'),
      dataField: 'work_value',
      lookup: useLookup({
        store: garageStore.getLocalizedWorkValues(),
        displayExpr: (item) => item.name
      }),
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }],
    },
    dataGridColumn.usePaymentTypeColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useDiscountColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useCurrencyColumn({ editors, visible: false, showInColumnChooser: false }),
    dataGridColumn.useExchangeRateColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useExchangeRateDateColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useNotesColumn({visible: false, showInColumnChooser: false}),
    {
      caption: i18n.t('common.priceVat', 2),
      dataField: 'includes_vat',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value) => {
        newData.includes_vat = includesVat.value = value
      }
    },
    {
      caption: i18n.t('common.expectedDeliveryDate'),
      dataField: 'expected_delivery_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerColumn({
      dataField: 'invoice_to_customer',
      caption: i18n.t('common.invoiceToCustomer'),
      editCellTemplate: 'customerEditor',
      setCellValue: async(newData, value, currentData) => {
        const currentBillingCustomer = value ? (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === value.id)
          : currentData.customer || null
        includesVat.value = getIncludesVatValue(currentBillingCustomer)

        newData.invoice_to_customer = currentBillingCustomer?.id
        newData.single_invoice = currentBillingCustomer?.single_invoice || null
      },
      validationRules: [{
        type: 'custom',
        message: i18n.t('error.validation.billingCustomerEqualsCustomer'),
        reevaluate: true,
        validationCallback: (e) => {
          return e.value !== e.data.customer_id
        }
      }],
      visible: false,
      showInColumnChooser: false,
    }),
    {
      caption: i18n.t('common.invoiceGrouping'),
      dataField: 'single_invoice',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.usepaidColumn({
      caption: webSiteStore.blankCaption,
      dataField: 'sale_document.paid',
      editCellTemplate: 'paidCellTemplate',
      visible: false,
      showInColumnChooser: false,
    }),
    dataGridColumn.usepaidAmountColumn({
      caption: i18n.t('common.paidAmount'),
      dataField: 'sale_document.paid_amount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    }),
  ]
}
